import { BackgroundColor } from '@/theme/types';
import { Link } from 'react-router';
import { LoginContext } from '@/util-react-router/providers/login-provider';
import { useContext, useMemo } from 'react';
import FlexcarWordmark2024 from '@/icons/flexcar-wordmark-2024';
import HeaderNavigationMenu from './header-navigation-menu';
import HeaderSheet, { HeaderSheetProps } from './header-sheet';
import { cn } from '@/shadcn-ui/utils';
import { colors } from '@/theme/colors';
import BreadcrumbBack from '../breadcrumb-back';
import Spacer from '../spacer';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';

export type HeaderVariant = 'default' | 'compact-mobile';

export interface HeaderProps extends Omit<HeaderSheetProps, 'isLoggedIn'> {
  hideLogo?: boolean;
  /**
   * Optional background color for the header sheet.
   * If not provided, `backgroundColor` prop will be used
   * as the background color.
   */
  headerSheetBackgroundColor?: BackgroundColor;
  zipcodeModalOpen?: boolean;
  handleZipcodeModalOpen?: () => void;
  handleZipcodeModalClose?: () => void;
  className?: string;
  logoFillColor?: string;
  hamburgerFillPrimary?: string;
  navLinkClassName?: string;
  /** Left hand content for header, appears immediately after flexcar logo */
  leftContent?: React.ReactNode;
  /** Only show Flexcar logo in center of header with no content on left or right side of the header for mobile */
  compactMobile?: boolean;
  /** Destination of back breadcrumb for compact mobile header variant */
  to?: string;
}

export default function Header({
  dark = false,
  hideLogo = false,
  hideHomeLink = false,
  hideCatalogLink = false,
  whyFlexcarButtonOnClickDelegate,
  // Dynamic header content
  leftContent,
  // HeaderSheet
  backgroundColor,
  headerSheetBackgroundColor,
  className,
  logoFillColor,
  navLinkClassName,
  hamburgerFillPrimary,
  compactMobile = false,
  to,
}: HeaderProps) {
  const isLoggedIn = useContext(LoginContext);
  const toInventory = useMemo(() => to?.includes('/inventory'), [to]);
  const { disableSpaNavigation } = useDisableSpaNavigation();

  return (
    <nav
      className={cn(
        'flex w-full items-center justify-between py-[16px] sm:py-[24px]',
        className,
        {
          'px-[16px] py-[8px] sm:py-[8px] md:px-0 md:py-[24px]': compactMobile,
        }
      )}
    >
      {/* Left side */}
      <div>
        {!hideLogo ? (
          <div
            className={cn('flex flex-row items-center justify-center gap-4', {
              'hidden md:flex': compactMobile,
            })}
          >
            <Link to="/" reloadDocument={disableSpaNavigation}>
              <FlexcarWordmark2024
                className="h-[40px] w-[107px]"
                fillPrimary={logoFillColor ?? (dark ? colors.neutral100 : colors.blue40)}
              />
            </Link>

            {leftContent}
          </div>
        ) : null}
        {to && compactMobile ? (
          <BreadcrumbBack
            to={to}
            toInventory={toInventory}
            className="my-auto justify-self-start md:hidden"
            hideSpacerForBreakpoints={['sm', 'md', 'lg', 'xl', '2xl']}
          />
        ) : null}
      </div>

      {/* Center */}
      <div
        className={cn({
          'block md:hidden': compactMobile,
          'hidden': !compactMobile,
        })}
      >
        <Link to="/" reloadDocument={disableSpaNavigation}>
          <FlexcarWordmark2024
            className="h-[32px] w-[85px]"
            fillPrimary={logoFillColor ?? (dark ? colors.neutral100 : colors.blue40)}
          />
        </Link>
      </div>

      {/* Right side */}
      <div>
        <HeaderNavigationMenu
          isLoggedIn={isLoggedIn}
          hideCatalogLink={hideCatalogLink}
          hideHomeLink={true}
          whyFlexcarButtonOnClickDelegate={whyFlexcarButtonOnClickDelegate}
          dark={dark}
          className={cn('hidden sm:block', {
            'hidden sm:hidden md:block': compactMobile,
          })}
          navLinkClassName={navLinkClassName}
        />
        <HeaderSheet
          isLoggedIn={isLoggedIn}
          hideHomeLink={hideHomeLink}
          hideCatalogLink={hideCatalogLink}
          dark={dark}
          className={cn('sm:hidden', {
            hidden: compactMobile,
          })}
          backgroundColor={headerSheetBackgroundColor ?? backgroundColor}
          hamburgerFillPrimary={hamburgerFillPrimary}
        />

        {/* Need this spacer to have logo centered for compact mobile header when there is back breadcrumb */}
        {compactMobile && to ? (
          <Spacer variant="width24" hideForBreakpoints={['md', 'lg', 'xl', '2xl']} />
        ) : null}
      </div>
    </nav>
  );
}
